import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Static label + a rotating word that wipes open, holds, then wipes shut.
 *
 * Ported from a TSX/Tailwind/motion component: this project is plain JSX with
 * hand-written CSS, and HeroSection is eagerly imported, so a new animation
 * dependency would land straight in the entry chunk.
 *
 * The original animates `width: 0 -> auto`, which CSS cannot transition on its
 * own. The word is measured instead and the clip is transitioned to that pixel
 * width — the span keeps its intrinsic width even while the parent is clipped
 * to 0, so one ref is enough. It is re-measured after web fonts settle and on
 * resize, since a fallback face measures differently from the real one.
 *
 * @param {string}   staticText     the part that never changes
 * @param {string[]} rotatingTexts  phrases to cycle
 * @param {number}   interval       ms a phrase stays fully open
 * @param {number}   duration       ms of the open/close wipe
 */
const TextLoop = ({
    staticText,
    rotatingTexts = [],
    interval = 3000,
    duration = 620,
    className = '',
}) => {
    const [index, setIndex] = useState(0);
    const [open, setOpen] = useState(true);
    const [width, setWidth] = useState(0);
    const wordRef = useRef(null);

    // ru/zh are lazily bundled. If this renders before the locale lands,
    // i18next may hand back the raw key rather than the array, so never
    // index into a value that is not one.
    const safe = Array.isArray(rotatingTexts) ? rotatingTexts.filter(Boolean) : [];
    const phrases = safe.length ? safe : [staticText].filter(Boolean);
    const current = phrases[index % phrases.length];

    // Measure before paint so the first word never flashes at zero width.
    useLayoutEffect(() => {
        const measure = () => {
            if (wordRef.current) setWidth(wordRef.current.offsetWidth);
        };
        measure();

        // A fallback face measures differently from Outfit / Plex Arabic.
        if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [current]);

    useEffect(() => {
        if (phrases.length < 2) return;

        // Anyone asking for less motion gets a plain swap, no wipe.
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        let closeTimer;
        const cycle = setInterval(() => {
            if (reduced) {
                setIndex((i) => (i + 1) % phrases.length);
                return;
            }
            setOpen(false);
            closeTimer = setTimeout(() => {
                setIndex((i) => (i + 1) % phrases.length);
                setOpen(true);
            }, duration);
        }, interval);

        return () => {
            clearInterval(cycle);
            clearTimeout(closeTimer);
        };
    }, [phrases.length, interval, duration]);

    if (!phrases.length) return null;

    return (
        <span className={`text-loop ${className}`.trim()}>
            {staticText ? <span className="text-loop-static">{staticText}</span> : null}
            <span className="text-loop-track">
                <span
                    className="text-loop-clip"
                    style={{
                        width: open ? `${width}px` : '0px',
                        opacity: open ? 1 : 0,
                        transitionDuration: `${duration}ms`,
                    }}
                >
                    <span ref={wordRef} className="text-loop-word">{current}</span>
                </span>
                <span className="text-loop-cursor" aria-hidden="true" />
            </span>
        </span>
    );
};

export default TextLoop;
